import type { ReactNode } from 'react';
import { Icon, Skeleton, type IconName } from '@capsico/ui';
import type { PaymentMethod } from '@capsico/contracts';
import { Button, Card, Notice, Sheet, Toggle } from './ui';

/*
 * Payment building blocks (W11 — capsico-customer-app-ui C18 + inherited W / AA). Presentation
 * only: every string, amount and handler comes from the page and its hooks (usePayment,
 * useRefundChoice, useInvoiceDownload, usePayReturn, useWallet).
 */

type Mark = { icon: IconName } | { text: string };

/** Line icon or monogram per way to pay (wallet apps get a monogram, never invented logos). */
const METHOD_MARK: Record<PaymentMethod, Mark> = {
  cod: { icon: 'home' },
  test: { icon: 'shield' },
  wallet: { icon: 'wallet' },
  upi: { icon: 'phone' },
  card: { icon: 'lock' },
  netbanking: { icon: 'store' },
  esewa: { text: 'e' },
  khalti: { text: 'K' },
  fonepay: { icon: 'scan' },
  imepay: { text: 'IME' },
  connectips: { icon: 'store' },
};

export function MethodMark({ method }: { method: PaymentMethod }) {
  const mark = METHOD_MARK[method];
  const brand = method === 'wallet';
  return (
    <span
      className={`inline-flex size-9 shrink-0 items-center justify-center rounded-field ${
        brand
          ? 'bg-brand-soft text-brand-ink'
          : 'bg-surface text-ink shadow-[inset_0_0_0_1px_var(--color-hair)]'
      }`}
    >
      {'icon' in mark ? (
        <Icon name={mark.icon} size={18} />
      ) : (
        <span
          className={`font-bold text-brand-ink ${mark.text.length > 2 ? 'text-micro' : 'text-sm'}`}
        >
          {mark.text}
        </span>
      )}
    </span>
  );
}

/** Radio dot (yellow fill + ink dot when on). */
function Radio({ on, dim = false }: { on: boolean; dim?: boolean }) {
  return (
    <span
      className={`flex size-5 shrink-0 items-center justify-center rounded-full ${
        on
          ? 'bg-brand shadow-[inset_0_0_0_1.5px_var(--color-ink)]'
          : 'shadow-[inset_0_0_0_1.5px_var(--color-control)]'
      } ${dim ? 'opacity-50' : ''}`}
    >
      {on && <span className="size-2 rounded-full bg-ink" />}
    </span>
  );
}

/** Capsico Money row at the top of "Pay with": balance, what this order uses, soonest expiry. */
export function WalletToggleRow({
  label,
  balance,
  hint,
  using,
  expiry,
  on,
  onToggle,
}: {
  label: string;
  balance: string;
  hint: string;
  using: boolean;
  expiry: string | null;
  on: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <MethodMark method="wallet" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">
          {label} <span className="font-bold tabular">{balance}</span>
        </p>
        <p className={`text-caption ${using ? 'font-medium text-positive' : 'text-ink-3'}`}>
          {hint}
        </p>
        {expiry && <p className="text-caption text-warning">{expiry}</p>}
      </div>
      <Toggle on={on} onChange={onToggle} label={label} />
    </div>
  );
}

export interface MethodRow {
  method: PaymentMethod;
  name: string;
  available: boolean;
  /** Note when available, the reason when not (COD tier). */
  caption: string | null;
}

/** C18 / W method list: one radio row per method; unavailable rows greyed with the reason. */
export function MethodList({
  rows,
  value,
  label,
  onChange,
}: {
  rows: MethodRow[];
  value: PaymentMethod | null;
  label: string;
  onChange: (method: PaymentMethod) => void;
}) {
  return (
    <div className="divide-y divide-hair" role="radiogroup" aria-label={label}>
      {rows.map((row) => {
        const on = row.method === value && row.available;
        return (
          <button
            key={row.method}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={!row.available}
            onClick={() => onChange(row.method)}
            className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-tile disabled:active:bg-transparent"
          >
            <MethodMark method={row.method} />
            <span className="min-w-0 flex-1">
              <span
                className={`block text-sm font-semibold ${row.available ? 'text-ink' : 'text-ink-3'}`}
              >
                {row.name}
              </span>
              {row.caption && <span className="block text-caption text-ink-3">{row.caption}</span>}
            </span>
            <Radio on={on} dim={!row.available} />
          </button>
        );
      })}
    </div>
  );
}

/** "Pay with" card: Capsico Money toggle (when there is a balance) above the method list. */
export function PaymentSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 px-1 text-sm font-bold text-ink">{title}</h2>
      <Card flush>
        <div className="divide-y divide-hair">{children}</div>
      </Card>
    </section>
  );
}

/** Line under the pay button: "₹50 Capsico Money applied" / secure-payments note. */
export function PayNote({ text, positive }: { text: string; positive: boolean }) {
  return (
    <p
      className={`mb-2 inline-flex items-center gap-1 text-caption font-semibold ${
        positive ? 'text-positive' : 'text-ink-3'
      }`}
    >
      <Icon name={positive ? 'check' : 'shield'} size={12} />
      {text}
    </p>
  );
}

/** Order waiting for its payment: countdown, resume the attempt, another way, cancel. */
export function PaymentPendingCard({
  title,
  body,
  timer,
  expired,
  labels,
  busy,
  canResume,
  canSwitch,
  cancelling,
  onPayNow,
  onAnother,
  onCancel,
}: {
  title: string;
  body: string;
  timer: string | null;
  expired: boolean;
  labels: { payNow: string; another: string; cancel: string };
  busy: boolean;
  canResume: boolean;
  canSwitch: boolean;
  cancelling: boolean;
  onPayNow: () => void;
  onAnother: () => void;
  onCancel: () => void;
}) {
  return (
    <section className="rounded-sheet bg-warning-bg p-4 shadow-[inset_0_0_0_1px_var(--color-hair)]">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-surface text-warning">
          <Icon name="clock" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-ink">{title}</p>
          <p className="mt-0.5 text-xs text-ink-2">{body}</p>
        </div>
        {timer && (
          <span
            className={`shrink-0 rounded-lg px-2.5 py-1 text-sm font-bold tabular ${
              expired ? 'bg-negative-bg text-negative' : 'bg-surface text-ink'
            }`}
            aria-live="polite"
          >
            {timer}
          </span>
        )}
      </div>
      <div className="mt-4 space-y-2.5">
        {canResume && !expired && (
          <Button full busy={busy} onClick={onPayNow}>
            <Icon name="lock" size={14} />
            {labels.payNow}
          </Button>
        )}
        {canSwitch && (
          <Button
            full
            tone={canResume && !expired ? 'outline' : 'brand'}
            disabled={busy}
            onClick={onAnother}
          >
            {labels.another}
          </Button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling}
          className="block w-full py-1 text-center text-xs font-semibold text-negative disabled:opacity-50"
        >
          {labels.cancel}
        </button>
      </div>
    </section>
  );
}

/** Pick another online way to pay for a waiting order. */
export function MethodPickerSheet({
  title,
  rows,
  busyMethod,
  onPick,
  onClose,
}: {
  title: string;
  rows: MethodRow[];
  busyMethod: PaymentMethod | null;
  onPick: (method: PaymentMethod) => void;
  onClose: () => void;
}) {
  return (
    <Sheet title={title} onClose={onClose}>
      <div className="-mx-5">
        <MethodList
          rows={rows.map((row) => ({ ...row, available: row.available && busyMethod === null }))}
          value={busyMethod}
          label={title}
          onChange={onPick}
        />
      </div>
    </Sheet>
  );
}

/** Fonepay QR: the payload to scan or copy, live "waiting" line, and "I've paid". */
export function QrSheet({
  title,
  body,
  payload,
  labels,
  checking,
  onCopy,
  onPaid,
  onClose,
}: {
  title: string;
  body: string;
  payload: string;
  labels: { copy: string; waiting: string; paid: string };
  checking: boolean;
  onCopy: () => void;
  onPaid: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      title={title}
      onClose={onClose}
      footer={
        <Button full busy={checking} onClick={onPaid}>
          {labels.paid}
        </Button>
      }
    >
      <p className="text-sm leading-6 text-ink-2">{body}</p>
      <div className="mt-4 rounded-xl bg-tile p-3">
        <p className="line-clamp-4 font-mono text-caption break-all text-ink">{payload}</p>
        <button
          type="button"
          onClick={onCopy}
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-ink"
        >
          <Icon name="copy" size={14} />
          {labels.copy}
        </button>
      </div>
      <p className="mt-4 inline-flex items-center gap-2 text-xs text-ink-2" aria-live="polite">
        <span className="size-3 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        {labels.waiting}
      </p>
    </Sheet>
  );
}

/** Small yes / no confirmation sheet (cancel an unpaid order). */
export function ConfirmSheet({
  title,
  body,
  yesLabel,
  noLabel,
  busy,
  onYes,
  onNo,
}: {
  title: string;
  body: string;
  yesLabel: string;
  noLabel: string;
  busy: boolean;
  onYes: () => void;
  onNo: () => void;
}) {
  return (
    <Sheet
      title={title}
      onClose={onNo}
      footer={
        <div className="grid grid-cols-2 gap-3">
          <Button tone="outline" onClick={onNo}>
            {noLabel}
          </Button>
          <Button tone="danger" busy={busy} onClick={onYes}>
            {yesLabel}
          </Button>
        </div>
      }
    >
      <p className="text-sm leading-6 text-ink-2">{body}</p>
    </Sheet>
  );
}

export interface RefundRowView {
  id: string;
  amount: string;
  reason: string;
  destination: string | null;
  status: string;
  tone: 'positive' | 'warning' | 'negative' | 'neutral';
  /** Present while the customer still has to pick where the money goes. */
  choice: {
    title: string;
    walletLabel: string;
    walletHint: string;
    sourceLabel: string | null;
    sourceHint: string;
    deadline: string | null;
  } | null;
}

const STATUS_TONE: Record<RefundRowView['tone'], string> = {
  positive: 'bg-positive-bg text-positive',
  warning: 'bg-warning-bg text-warning',
  negative: 'bg-negative-bg text-negative',
  neutral: 'bg-tile text-ink-2',
};

/** "Where should ₹120 go?" — Capsico Money now (+bonus) or back to the original method. */
export function RefundChoiceCard({
  choice,
  busy,
  onWallet,
  onSource,
}: {
  choice: NonNullable<RefundRowView['choice']>;
  busy: boolean;
  onWallet: () => void;
  onSource: () => void;
}) {
  return (
    <div className="mt-3 rounded-xl bg-brand-soft p-3">
      <p className="text-sm font-bold text-ink">{choice.title}</p>
      {choice.deadline && <p className="mt-0.5 text-caption text-ink-2">{choice.deadline}</p>}
      <div className="mt-3 space-y-2">
        <button
          type="button"
          disabled={busy}
          onClick={onWallet}
          className="flex w-full items-center gap-3 rounded-xl bg-surface px-3 py-3 text-left shadow-[inset_0_0_0_1.5px_var(--color-brand-600)] disabled:opacity-60"
        >
          <MethodMark method="wallet" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-ink">{choice.walletLabel}</span>
            <span className="block text-caption text-positive">{choice.walletHint}</span>
          </span>
          <Icon name="chevron-right" size={16} />
        </button>
        {choice.sourceLabel && (
          <button
            type="button"
            disabled={busy}
            onClick={onSource}
            className="flex w-full items-center gap-3 rounded-xl bg-surface px-3 py-3 text-left shadow-[inset_0_0_0_1px_var(--color-hair)] disabled:opacity-60"
          >
            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-field bg-surface text-ink shadow-[inset_0_0_0_1px_var(--color-hair)]">
              <Icon name="undo" size={18} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-ink">{choice.sourceLabel}</span>
              <span className="block text-caption text-ink-3">{choice.sourceHint}</span>
            </span>
            <Icon name="chevron-right" size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

/** One refund: amount, why, where it goes and its status; the choice card when it waits. */
export function RefundItem({
  row,
  busy,
  onChoose,
}: {
  row: RefundRowView;
  busy: boolean;
  onChoose: (destination: 'wallet' | 'source') => void;
}) {
  return (
    <div className="py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">{row.reason}</p>
          {row.destination && <p className="text-caption text-ink-3">{row.destination}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-sm font-bold text-ink tabular">{row.amount}</span>
          <span
            className={`rounded-full px-2 py-0.5 text-micro font-semibold ${STATUS_TONE[row.tone]}`}
          >
            {row.status}
          </span>
        </div>
      </div>
      {row.choice && (
        <RefundChoiceCard
          choice={row.choice}
          busy={busy}
          onWallet={() => onChoose('wallet')}
          onSource={() => onChoose('source')}
        />
      )}
    </div>
  );
}

/** Refunds card on the order page. */
export function RefundsCard({
  title,
  rows,
  busyId,
  onChoose,
}: {
  title: string;
  rows: RefundRowView[];
  busyId: string | null;
  onChoose: (refundId: string, destination: 'wallet' | 'source') => void;
}) {
  return (
    <Card>
      <h2 className="text-sm font-bold text-ink">{title}</h2>
      <div className="mt-1 divide-y divide-hair">
        {rows.map((row) => (
          <RefundItem
            key={row.id}
            row={row}
            busy={busyId === row.id}
            onChoose={(destination) => onChoose(row.id, destination)}
          />
        ))}
      </div>
    </Card>
  );
}

export interface InvoiceRowView {
  id: string;
  kind: 'food' | 'fees';
  title: string;
  subtitle: string;
  amount: string;
}

/** Tax invoices with a download button each. */
export function InvoicesCard({
  title,
  rows,
  downloadLabel,
  downloading,
  onDownload,
}: {
  title: string;
  rows: InvoiceRowView[];
  downloadLabel: string;
  downloading: string | null;
  onDownload: (kind: 'food' | 'fees') => void;
}) {
  return (
    <Card>
      <h2 className="text-sm font-bold text-ink">{title}</h2>
      <div className="mt-1 divide-y divide-hair">
        {rows.map((row) => (
          <div key={row.id} className="flex items-center gap-3 py-3">
            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
              <Icon name="receipt" size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{row.title}</p>
              <p className="truncate text-caption text-ink-3 tabular">{row.subtitle}</p>
            </div>
            <span className="shrink-0 text-xs text-ink-2 tabular">{row.amount}</span>
            <Button
              size="sm"
              tone="outline"
              busy={downloading === row.kind}
              aria-label={`${downloadLabel} ${row.title}`}
              onClick={() => onDownload(row.kind)}
            >
              <Icon name="arrow-down" size={14} />
              {downloadLabel}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** "Confirming your payment…" — shaped skeleton while the server checks with the gateway. */
export function ConfirmingCard({
  title,
  body,
  slowNote,
  action,
}: {
  title: string;
  body: string;
  slowNote: string | null;
  action?: ReactNode;
}) {
  return (
    <Card className="text-center">
      <span className="mx-auto inline-flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
        <Icon name="shield" size={26} />
      </span>
      <h1 className="mt-3 text-title font-bold text-ink">{title}</h1>
      <p className="mt-1 text-xs leading-5 text-ink-2">{body}</p>
      <div className="mt-5 space-y-2 text-left" role="status" aria-label={title}>
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      {slowNote && <p className="mt-4 text-caption text-ink-3">{slowNote}</p>}
      {action && <div className="mt-4">{action}</div>}
    </Card>
  );
}

/** The attempt failed: why, then "Try again" (another way) and "Cancel order". */
export function PaymentFailedCard({
  title,
  body,
  children,
}: {
  title: string;
  body: string | null;
  children?: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <Notice tone="negative" title={title}>
        {body}
      </Notice>
      {children && <div className="space-y-2.5">{children}</div>}
    </div>
  );
}
